import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Scissors, TreePine, Flower2, ChevronRight, Check, Phone, Facebook, Sun, Waves, Palmtree, Star, MessageCircle, Sword, Trash2, Droplets } from "lucide-react";
import BookingWizard from "@/components/BookingWizard";
import AuthModal from "@/components/AuthModal";
import NeighborhoodCoupon from "@/components/NeighborhoodCoupon";
import axios from "axios";

const API = "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a07fe825-978d-42af-ad0d-66b05bf66e9f/artifacts/6h7nm3ap_Photoroom_20260215_225825.png";
const MOWER_URL = "https://customer-assets.emergentagent.com/job_a07fe825-978d-42af-ad0d-66b05bf66e9f/artifacts/nt4dvwbo_Photoroom_20260215_224930.png";
const BG_LOGO_URL = "https://customer-assets.emergentagent.com/job_a07fe825-978d-42af-ad0d-66b05bf66e9f/artifacts/6h7nm3ap_Photoroom_20260215_225825.png";

// John Deere Yellow
const JD_YELLOW = "#FFDE00";

// Okinawa-themed decorative icons component
const OkinawaDecorations = () => (
  <>
    {/* Floating palm tree icon - top left */}
    <div className="absolute top-20 left-4 md:left-10 opacity-30 animate-pulse">
      <Palmtree className="w-8 h-8 md:w-12 md:h-12 text-[#39FF14]" />
    </div>
    {/* Sun icon - top right */}
    <div className="absolute top-16 right-8 md:right-16 opacity-40">
      <Sun className="w-10 h-10 md:w-14 md:h-14" style={{ color: JD_YELLOW }} />
    </div>
    {/* Wave icon - bottom left */}
    <div className="absolute bottom-10 left-8 opacity-25">
      <Waves className="w-16 h-16 text-blue-400" />
    </div>
    {/* Floating shuriken - mid right */}
    <div className="absolute top-1/3 right-4 md:right-8 opacity-20 animate-spin" style={{ animationDuration: '8s' }}>
      <ShurikenIcon className="w-10 h-10 md:w-16 md:h-16 text-[#39FF14]" />
    </div>
  </>
);

// Hibiscus flower SVG component (Okinawa's iconic flower)
const HibiscusIcon = ({ className, style }) => (
  <svg viewBox="0 0 24 24" className={className} style={style} fill="currentColor">
    <path d="M12 2C11.5 4 10 5.5 8 6.5C6 7.5 4 7 2 6C3 8 4 10 4 12C4 14 3 16 2 18C4 17 6 16.5 8 17.5C10 18.5 11.5 20 12 22C12.5 20 14 18.5 16 17.5C18 16.5 20 17 22 18C21 16 20 14 20 12C20 10 21 8 22 6C20 7 18 7.5 16 6.5C14 5.5 12.5 4 12 2Z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

// Shisa (Okinawan lion-dog) SVG component
const ShisaIcon = ({ className, style }) => (
  <svg viewBox="0 0 24 24" className={className} style={style} fill="currentColor">
    <path d="M12 2L9 5H5V9L2 12L5 15V19H9L12 22L15 19H19V15L22 12L19 9V5H15L12 2ZM12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7ZM9 10V11H10V10H9ZM14 10V11H15V10H14ZM10 14C10 14 11 15 12 15C13 15 14 14 14 14H10Z"/>
  </svg>
);

// Ninja Shuriken (throwing star) SVG component
const ShurikenIcon = ({ className, style }) => (
  <svg viewBox="0 0 24 24" className={className} style={style} fill="currentColor">
    <path d="M12 2L14 8L12 12L10 8L12 2ZM12 22L10 16L12 12L14 16L12 22ZM2 12L8 10L12 12L8 14L2 12ZM22 12L16 14L12 12L16 10L22 12ZM4.93 4.93L9.17 9.17L12 12L8.83 8.83L4.93 4.93ZM19.07 19.07L14.83 14.83L12 12L15.17 15.17L19.07 19.07ZM4.93 19.07L8.83 15.17L12 12L9.17 14.83L4.93 19.07ZM19.07 4.93L15.17 8.83L12 12L14.83 9.17L19.07 4.93Z"/>
  </svg>
);

// Ninja mask SVG component
const NinjaMaskIcon = ({ className, style }) => (
  <svg viewBox="0 0 24 24" className={className} style={style} fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12C2 14.5 3 16.75 4.64 18.36L6.5 15H17.5L19.36 18.36C21 16.75 22 14.5 22 12C22 6.48 17.52 2 12 2ZM7 11C6.45 11 6 10.55 6 10C6 9.45 6.45 9 7 9C7.55 9 8 9.45 8 10C8 10.55 7.55 11 7 11ZM17 11C16.45 11 16 10.55 16 10C16 9.45 16.45 9 17 9C17.55 9 18 9.45 18 10C18 10.55 17.55 11 17 11Z"/>
    <path d="M5 13H19L20 14H4L5 13Z"/>
  </svg>
);

const LandingPage = () => {
  const { initiateGoogleLogin } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [hoveredService, setHoveredService] = useState(null);
  const [showBookingWizard, setShowBookingWizard] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [subscriptionsEnabled, setSubscriptionsEnabled] = useState(true);
  const [additionalServicesEnabled, setAdditionalServicesEnabled] = useState(true);
  const [neighborhoodDiscountEnabled, setNeighborhoodDiscountEnabled] = useState(true);
  const [cmsContent, setCmsContent] = useState({});

  useEffect(() => {
    // Fetch settings and CMS content
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSubscriptionsEnabled(response.data.subscriptions_enabled !== false);
        setAdditionalServicesEnabled(response.data.additional_services_enabled !== false);
        setNeighborhoodDiscountEnabled(response.data.neighborhood_discount_enabled !== false);
      } catch (error) {
        console.error("Failed to fetch settings:", error);
        setSubscriptionsEnabled(true);
      }
    };

    const fetchCmsContent = async () => {
      try {
        const response = await axios.get(`${API}/cms/content`);
        // Merge saved content over defaults
        const defaults = response.data.defaults?.landing || {};
        const saved = response.data.content?.landing || {};
        const merged = {};
        Object.keys(defaults).forEach(section => {
          merged[section] = { ...defaults[section] };
        });
        Object.keys(saved).forEach(section => {
          if (!merged[section]) merged[section] = {};
          Object.keys(saved[section]).forEach(key => {
            merged[section][key] = saved[section][key];
          });
        });
        setCmsContent(merged);
      } catch (error) {
        console.error("Failed to fetch CMS content:", error);
      }
    };

    fetchSettings();
    fetchCmsContent();
    
    // Track page visit
    const trackVisit = async () => {
      try {
        await axios.post(`${API}/track/visit`);
      } catch (error) {
        // Silently fail - visitor tracking is not critical
        console.debug("Visit tracking failed:", error);
      }
    };
    trackVisit();
  }, []);

  const openAuthModal = (mode = "login") => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const services = [
    {
      icon: Scissors,
      title: t("landing.services.mowing", "Lawn Mowing"),
      description: t("landing.services.mowingDesc", "Regular mowing to keep your lawn looking perfect year-round"),
      interactive: true,
    },
    {
      icon: TreePine,
      title: t("landing.services.edging", "Edging & Trimming"),
      description: t("landing.services.edgingDesc", "Clean, precise edges along walkways and flower beds"),
      interactive: false,
    },
    {
      icon: Flower2,
      title: t("landing.services.cleanup", "Yard Cleanup"),
      description: t("landing.services.cleanupDesc", "Complete debris removal and seasonal cleanup services"),
      interactive: false,
    },
  ];

  const handleServiceClick = (service) => {
    if (service.interactive) {
      setShowBookingWizard(true);
    }
  };

  // CMS helper - get content with fallback
  const cms = (section, key, fallback = "") => cmsContent?.[section]?.[key] || fallback;

  const subscriptionPlans = [
    {
      name: cms("subscriptions", "monthly_name", "Monthly"),
      discount: cms("subscriptions", "monthly_discount", "10% OFF"),
      description: cms("subscriptions", "monthly_desc", "Perfect for regular lawn care"),
      features: [
        cms("subscriptions", "monthly_feature_1", "Priority scheduling"),
        cms("subscriptions", "monthly_feature_2", "10% discount on all services"),
        cms("subscriptions", "monthly_feature_3", "Email reminders"),
      ].filter(Boolean),
      tiers: [
        { label: "1x / month", description: "One cut per month" },
        { label: "2x / month", description: "Two cuts per month" },
      ],
    },
    {
      name: cms("subscriptions", "yearly_name", "Yearly"),
      discount: cms("subscriptions", "yearly_discount", "25% OFF"),
      badge: cms("subscriptions", "yearly_badge", "BEST VALUE"),
      description: cms("subscriptions", "yearly_desc", "Best value for committed customers"),
      features: [
        cms("subscriptions", "yearly_feature_1", "Priority scheduling"),
        cms("subscriptions", "yearly_feature_2", "25% discount on all services"),
        cms("subscriptions", "yearly_feature_3", "Email & SMS reminders"),
        cms("subscriptions", "yearly_feature_4", "Free seasonal checkup"),
      ].filter(Boolean),
      popular: true,
      tiers: [
        { label: "1x / month", description: "One cut per month" },
        { label: "2x / month", description: "Two cuts per month" },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative">
      {/* Fixed Background Logo */}
      <div 
        className="fixed inset-0 z-0 flex items-center justify-center pointer-events-none"
      >
        <img 
          src={BG_LOGO_URL}
          alt=""
          className="w-[600px] h-[600px] md:w-[800px] md:h-[800px] object-contain"
          style={{
            opacity: 0.08,
            filter: 'brightness(1.2)',
          }}
        />
      </div>

      {/* Hero Section */}
      <header className="relative z-10 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1558904541-efa843a96f01?w=1920&q=80"
            alt="Lush green lawn"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/90 via-[#0A0A0A]/70 to-[#0A0A0A]"></div>
        </div>

        {/* Okinawa Decorative Elements */}
        <OkinawaDecorations />

        {/* Nav */}
        <nav className="relative z-10 flex items-center justify-between p-4 md:px-12">
          <div className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="Zig Zag Lawncare" 
              className="h-16 w-auto object-contain rounded-lg"
            />
            {/* Small hibiscus next to logo */}
            <HibiscusIcon className="w-6 h-6 hidden md:block" style={{ color: JD_YELLOW }} />
          </div>
          <div className="flex items-center gap-4">
            <Link to="/staff-login">
              <Button
                data-testid="staff-login-link"
                variant="ghost"
                className="text-[#A1A1AA] hover:text-white hover:bg-[#27272A]"
              >
                {t("landing.header.staffLogin", "Staff Login")}
              </Button>
            </Link>
            <Button
              data-testid="customer-login-btn"
              onClick={() => openAuthModal("login")}
              className="bg-[#39FF14] text-black hover:bg-[#32D711] hover:shadow-[0_0_15px_rgba(57,255,20,0.6)] transition-all duration-300 font-bold uppercase tracking-wider rounded-full px-6"
            >
              {t("landing.header.customerLogin", "Customer Login")}
            </Button>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 px-6 md:px-12 py-16 md:py-28">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* Ninja catchphrase badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-3 bg-[#39FF14]/10 border border-[#39FF14]/30">
                <ShurikenIcon className="w-4 h-4 text-[#39FF14] animate-spin" style={{ animationDuration: '3s' }} />
                <span className="text-sm font-bold text-[#39FF14] uppercase tracking-wider">{t("landing.hero.ninjaTagline", "Your Ultimate Grass Ninja")}</span>
                <ShurikenIcon className="w-4 h-4 text-[#39FF14] animate-spin" style={{ animationDuration: '3s' }} />
              </div>
              
              {/* Okinawa badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ backgroundColor: `${JD_YELLOW}20`, border: `1px solid ${JD_YELLOW}50` }}>
                <Sun className="w-4 h-4" style={{ color: JD_YELLOW }} />
                <span className="text-sm font-medium" style={{ color: JD_YELLOW }}>{t("landing.hero.badge", "Serving Okinawa On & Off Base")}</span>
                <Palmtree className="w-4 h-4" style={{ color: JD_YELLOW }} />
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight uppercase mb-4">
                {t("landing.hero.title", "Zig Zag")}
                <span className="text-[#39FF14] neon-text block">{t("landing.hero.subtitle", "Lawncare")}</span>
                <span style={{ color: JD_YELLOW }} className="text-2xl sm:text-3xl lg:text-4xl">{t("landing.hero.location", "Okinawa")}</span>
              </h1>
              
              {/* Ninja sub-tagline */}
              <p className="text-[#39FF14] font-semibold text-sm md:text-base mb-2 flex items-center gap-2">
                <Sword className="w-4 h-4" />
                {t("landing.hero.ninjaSubtag", "Not Silent, But Swift!")}
                <NinjaMaskIcon className="w-5 h-5" style={{ color: '#39FF14' }} />
              </p>
              
              <p className="text-base md:text-lg text-[#A1A1AA] mb-6 max-w-2xl">
                {t("landing.hero.description", "Professional lawn care services on and off-base. Years of experience keeping Okinawa's lawns pristine. Best prices guaranteed!")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="hero-book-now-btn"
                  onClick={() => openAuthModal("signup")}
                  className="bg-[#39FF14] text-black hover:bg-[#32D711] hover:shadow-[0_0_15px_rgba(57,255,20,0.6)] transition-all duration-300 font-bold uppercase tracking-wider rounded-full px-8 py-6 text-lg"
                >
                  {t("landing.hero.bookNow", "BOOK NOW")}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
                <a 
                  href="https://www.facebook.com/zigzagokinawa" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button
                    data-testid="hero-facebook-btn"
                    variant="outline"
                    className="border-[#1877F2] text-[#1877F2] hover:bg-[#1877F2] hover:text-white rounded-full px-8 py-6 text-lg transition-all duration-300 w-full sm:w-auto"
                  >
                    <Facebook className="mr-2 w-5 h-5" />
                    {t("landing.hero.dmFacebook", "DM Us on Facebook")}
                  </Button>
                </a>
              </div>
            </div>
            {/* Lawnmower Image */}
            <div className="hidden lg:flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-[#39FF14]/20 blur-3xl rounded-full scale-75"></div>
                <div className="relative z-10 p-4">
                  <img 
                    src={MOWER_URL}
                    alt="Professional Lawnmower"
                    className="max-w-md w-full object-contain drop-shadow-[0_0_30px_rgba(57,255,20,0.3)]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Services Section */}
      <section className="relative z-10 px-6 md:px-12 py-16 md:py-24">
        <div className="text-center mb-12">
          {/* Decorative icons around title */}
          <div className="flex items-center justify-center gap-4 mb-4">
            <HibiscusIcon className="w-8 h-8 hidden md:block" style={{ color: JD_YELLOW }} />
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase">
              {t("landing.services.title", "Our Services")}
            </h2>
            <HibiscusIcon className="w-8 h-8 hidden md:block" style={{ color: JD_YELLOW }} />
          </div>
          <p className="text-[#A1A1AA] text-base max-w-2xl mx-auto">
            {t("landing.services.subtitle", "Professional lawn care tailored for Okinawa")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {services.map((service, index) => (
            <div
              key={index}
              data-testid={`service-card-${index}`}
              onClick={() => handleServiceClick(service)}
              className={`bg-[#121212] border border-[#27272A] rounded-xl p-6 transition-all duration-300 relative overflow-hidden ${
                service.interactive ? "cursor-pointer" : "cursor-default"
              } ${
                hoveredService === index ? "border-[#39FF14]/50 shadow-[0_0_15px_rgba(57,255,20,0.1)] -translate-y-1" : ""
              }`}
              onMouseEnter={() => setHoveredService(index)}
              onMouseLeave={() => setHoveredService(null)}
            >
              {/* Yellow accent stripe */}
              <div className="absolute top-0 right-0 w-16 h-1" style={{ backgroundColor: JD_YELLOW }}></div>
              
              <div className={`w-14 h-14 rounded-lg flex items-center justify-center mb-4 transition-all duration-300 ${
                hoveredService === index ? "bg-[#39FF14]" : "bg-[#064E3B]"
              }`}>
                <service.icon className={`w-7 h-7 ${hoveredService === index ? "text-black" : "text-[#39FF14]"}`} />
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-2">{service.title}</h3>
              <p className="text-[#A1A1AA] text-sm">{service.description}</p>
              {service.interactive && (
                <div className={`mt-4 flex items-center gap-2 text-sm font-medium transition-all duration-300 ${
                  hoveredService === index 
                    ? "drop-shadow-[0_0_10px_rgba(57,255,20,0.8)]" 
                    : ""
                }`} style={{ color: hoveredService === index ? JD_YELLOW : '#39FF14' }}>
                  <span className={`uppercase tracking-wide ${hoveredService === index ? "font-bold text-lg" : ""}`}>
                    {hoveredService === index ? "START NOW" : "Get a Quote"}
                  </span>
                  <ChevronRight className={`transition-all duration-300 ${hoveredService === index ? "w-5 h-5 translate-x-1" : "w-4 h-4"}`} />
                </div>
              )}
              {/* FREE with grass cut badge for Bush/Tree Trimming and Weeding */}
              {(service.title === "Bush/Tree Trimming" || service.title === "Weeding") && (
                <div className={`mt-4 transition-all duration-300 ${
                  hoveredService === index ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
                }`}>
                  <span className="font-bold text-lg uppercase tracking-wide drop-shadow-[0_0_10px_rgba(255,222,0,0.8)]" style={{ color: JD_YELLOW }}>
                    FREE with grass cut!
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* One-Time Service Section */}
      <section className="relative z-10 px-6 md:px-12 py-16 md:py-24 bg-[#0D0D0D]/90">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase mb-4">
              {cms("one_time", "section_title", "One-Time Service")}
            </h2>
            <p className="text-[#A1A1AA] text-base max-w-2xl mx-auto">
              {cms("one_time", "section_subtitle", "Need a single cut? No commitment required")}
            </p>
          </div>

          <div 
            onClick={() => setShowBookingWizard(true)}
            className="bg-[#121212] border-2 border-[#27272A] hover:border-[#39FF14] rounded-xl p-8 cursor-pointer transition-all duration-300 hover:shadow-[0_0_20px_rgba(57,255,20,0.15)]"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-[#064E3B] rounded-xl flex items-center justify-center">
                  <Scissors className="w-8 h-8 text-[#39FF14]" />
                </div>
                <div>
                  <h3 className="font-heading text-2xl font-bold text-white uppercase">{cms("one_time", "card_title", "Single Cut")}</h3>
                  <p className="text-[#A1A1AA]">{cms("one_time", "card_desc", "One-time grass cutting service")}</p>
                </div>
              </div>
              <div className="flex flex-col items-center md:items-end gap-2">
                <p className="text-[#A1A1AA] text-sm">{cms("one_time", "price_label", "Starting from")}</p>
                <p className="text-[#39FF14] text-3xl font-bold">{cms("one_time", "starting_price", "¥3,000")}</p>
                <p className="text-[#A1A1AA] text-xs">{cms("one_time", "price_note", "Price varies by yard size")}</p>
              </div>
              <Button
                data-testid="one-time-cut-btn"
                className="bg-[#39FF14] text-black hover:bg-[#32D711] hover:shadow-[0_0_15px_rgba(57,255,20,0.6)] transition-all duration-300 font-bold uppercase tracking-wider rounded-full px-8 py-6"
              >
                {cms("one_time", "cta_button", "Get Quote")}
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Subscription Plans - Only show if enabled */}
      {subscriptionsEnabled && (
      <section className="px-6 md:px-12 py-16 md:py-24 bg-[#0A0A0A]/90 relative">
        {/* Decorative wave at top */}
        <div className="absolute top-0 left-0 right-0 h-1" style={{ background: `linear-gradient(90deg, transparent, ${JD_YELLOW}, transparent)` }}></div>
        
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Star className="w-6 h-6" style={{ color: JD_YELLOW }} />
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase">
              {cms("subscriptions", "section_title", t("landing.subscriptions.title", "Subscription Plans"))}
            </h2>
            <Star className="w-6 h-6" style={{ color: JD_YELLOW }} />
          </div>
          <p className="text-[#A1A1AA] text-base max-w-2xl mx-auto">
            {cms("subscriptions", "section_subtitle", t("landing.subscriptions.subtitle", "Save money with our subscription options"))}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {subscriptionPlans.map((plan, index) => (
            <div
              key={index}
              data-testid={`subscription-plan-${plan.name.toLowerCase()}`}
              onClick={() => openAuthModal("signup")}
              className={`relative bg-[#121212] rounded-xl p-8 transition-all duration-300 cursor-pointer hover:scale-[1.02] ${
                plan.popular
                  ? "border-2 shadow-[0_0_20px_rgba(255,222,0,0.2)] hover:shadow-[0_0_30px_rgba(255,222,0,0.4)]"
                  : "border border-[#27272A] hover:border-[#39FF14]/50 hover:shadow-[0_0_20px_rgba(57,255,20,0.15)]"
              }`}
              style={plan.popular ? { borderColor: JD_YELLOW } : {}}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-black px-4 py-1 rounded-full text-sm font-bold uppercase" style={{ backgroundColor: JD_YELLOW }}>
                  {plan.badge || "Best Value"}
                </div>
              )}
              <div className="text-center mb-6">
                <h3 className="font-heading text-2xl font-bold text-white uppercase mb-2">{plan.name}</h3>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-5xl font-bold" style={{ color: plan.popular ? JD_YELLOW : '#39FF14' }}>{plan.discount}</span>
                </div>
                <p className="text-[#A1A1AA] text-sm">{plan.description}</p>
              </div>
              {/* Frequency Tiers */}
              <div className="grid grid-cols-2 gap-2 mb-6">
                {plan.tiers.map((tier, ti) => (
                  <div key={ti} className={`text-center p-3 rounded-lg border ${plan.popular ? "border-[#FFDE00]/30 bg-[#FFDE00]/5" : "border-[#39FF14]/30 bg-[#39FF14]/5"}`}>
                    <p className="font-bold text-white text-sm">{tier.label}</p>
                    <p className="text-[#A1A1AA] text-xs">{tier.description}</p>
                  </div>
                ))}
              </div>
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center gap-3 text-[#EDEDED]">
                    <Check className="w-5 h-5" style={{ color: plan.popular ? JD_YELLOW : '#39FF14' }} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button
                className={`w-full font-bold uppercase tracking-wider rounded-full py-6 transition-all duration-300 ${
                  plan.popular 
                    ? "bg-[#FFDE00] text-black hover:bg-[#FFE833] hover:shadow-[0_0_15px_rgba(255,222,0,0.6)]" 
                    : "bg-[#39FF14] text-black hover:bg-[#32D711] hover:shadow-[0_0_15px_rgba(57,255,20,0.6)]"
                }`}
              >
                {t("landing.subscriptions.subscribeNow", "Subscribe Now")}
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* Additional Services Section */}
      {additionalServicesEnabled && (
      <section className="px-6 md:px-12 py-16 md:py-24 bg-[#0A0A0A] relative" data-testid="additional-services-section">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#39FF14] to-transparent"></div>
        
        <div className="text-center mb-12">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/30 mb-4">
            {cms("additional_services", "badge_text", "NEW")}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase mb-3">
            {cms("additional_services", "section_title", "ADDITIONAL SERVICES")}
          </h2>
          <p className="text-[#A1A1AA] text-base max-w-2xl mx-auto">
            {cms("additional_services", "section_subtitle", "More ways we can keep your property looking great")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {/* Trash Bin Cleaning */}
          <div className="group bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden hover:border-[#39FF14]/40 transition-all duration-300 hover:shadow-[0_0_20px_rgba(57,255,20,0.1)]" data-testid="service-trash-bin">
            <div className="h-48 overflow-hidden relative">
              <img 
                src={cms("additional_services", "trash_bin_image", "https://images.unsplash.com/photo-1605602560709-bd03f0366628?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85")}
                alt="Trash Bin Cleaning"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent"></div>
              <div className="absolute top-3 left-3">
                <div className="w-10 h-10 rounded-lg bg-[#39FF14]/20 backdrop-blur-sm flex items-center justify-center border border-[#39FF14]/30">
                  <Trash2 className="w-5 h-5 text-[#39FF14]" />
                </div>
              </div>
            </div>
            <div className="p-6">
              <h3 className="font-heading text-xl font-bold text-white uppercase mb-2">
                {cms("additional_services", "trash_bin_title", "Trash Bin Cleaning")}
              </h3>
              <p className="text-[#A1A1AA] text-sm mb-5 leading-relaxed">
                {cms("additional_services", "trash_bin_desc", "Deep cleaning and sanitizing of your trash bins to eliminate odors, bacteria, and buildup")}
              </p>
              <Button
                onClick={() => openAuthModal("signup")}
                className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold uppercase tracking-wider rounded-full"
              >
                {cms("additional_services", "cta_button", "REQUEST QUOTE")}
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Gutter & Drainage Cleaning */}
          <div className="group bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden hover:border-[#39FF14]/40 transition-all duration-300 hover:shadow-[0_0_20px_rgba(57,255,20,0.1)]" data-testid="service-gutter">
            <div className="h-48 overflow-hidden relative">
              <img 
                src={cms("additional_services", "gutter_image", "https://customer-assets.emergentagent.com/job_e3adbda9-f4db-46cf-a8ca-cf2d8ccdb7ef/artifacts/ps0zpixd_800x533_ea1e9d427fb5664c32c517a73e421e58_5303067777d093e6b9c5ec2e6da131d9c0f02fcf.jpg")}
                alt="Gutter Cleaning"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent"></div>
              <div className="absolute top-3 left-3">
                <div className="w-10 h-10 rounded-lg bg-[#39FF14]/20 backdrop-blur-sm flex items-center justify-center border border-[#39FF14]/30">
                  <Droplets className="w-5 h-5 text-[#39FF14]" />
                </div>
              </div>
            </div>
            <div className="p-6">
              <h3 className="font-heading text-xl font-bold text-white uppercase mb-2">
                {cms("additional_services", "gutter_title", "Gutter & Drainage Cleaning")}
              </h3>
              <p className="text-[#A1A1AA] text-sm mb-5 leading-relaxed">
                {cms("additional_services", "gutter_desc", "High-pressure washing of Japanese-style gutters and drainage channels to prevent blockages and flooding")}
              </p>
              <Button
                onClick={() => openAuthModal("signup")}
                className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold uppercase tracking-wider rounded-full"
              >
                {cms("additional_services", "cta_button", "REQUEST QUOTE")}
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Pressure Washing */}
          <div className="group bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden hover:border-[#39FF14]/40 transition-all duration-300 hover:shadow-[0_0_20px_rgba(57,255,20,0.1)]" data-testid="service-pressure-wash">
            <div className="h-48 overflow-hidden relative">
              <img 
                src={cms("additional_services", "pressure_wash_image", "https://images.unsplash.com/photo-1692133188474-8c5591e6a6a8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85")}
                alt="Pressure Washing"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent"></div>
              <div className="absolute top-3 left-3">
                <div className="w-10 h-10 rounded-lg bg-[#39FF14]/20 backdrop-blur-sm flex items-center justify-center border border-[#39FF14]/30">
                  <Waves className="w-5 h-5 text-[#39FF14]" />
                </div>
              </div>
            </div>
            <div className="p-6">
              <h3 className="font-heading text-xl font-bold text-white uppercase mb-2">
                {cms("additional_services", "pressure_wash_title", "Pressure Washing")}
              </h3>
              <p className="text-[#A1A1AA] text-sm mb-5 leading-relaxed">
                {cms("additional_services", "pressure_wash_desc", "Professional high-pressure cleaning for driveways, patios, walls, and outdoor surfaces")}
              </p>
              <Button
                onClick={() => openAuthModal("signup")}
                className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold uppercase tracking-wider rounded-full"
              >
                {cms("additional_services", "cta_button", "REQUEST QUOTE")}
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Brand Showcase Section */}
      <section className="relative z-10 px-6 md:px-12 py-16 md:py-24">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-[#121212] to-[#0a1a0a] border border-[#39FF14]/30 rounded-3xl p-8 md:p-12 relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#39FF14]/10 blur-3xl rounded-full"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-[#FFDE00]/10 blur-3xl rounded-full"></div>
            
            <div className="relative flex flex-col md:flex-row items-center gap-8 md:gap-12">
              {/* Large Logo */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <div className="absolute inset-0 bg-[#39FF14]/20 blur-2xl rounded-full scale-110"></div>
                  <img 
                    src={LOGO_URL}
                    alt="Zig Zag Lawncare Logo"
                    className="relative w-40 h-40 md:w-56 md:h-56 object-contain rounded-3xl border-2 border-[#39FF14]/50 shadow-[0_0_40px_rgba(57,255,20,0.3)] bg-black/30 p-2"
                  />
                </div>
              </div>
              
              {/* Brand Info */}
              <div className="text-center md:text-left flex-1">
                <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
                  <ShurikenIcon className="w-6 h-6 text-[#39FF14]" />
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase">
                    Zig Zag Lawncare
                  </h2>
                  <ShurikenIcon className="w-6 h-6 text-[#39FF14]" />
                </div>
                <p className="text-[#39FF14] font-semibold text-lg mb-3">Your Ultimate Grass Ninja</p>
                <p className="text-[#A1A1AA] text-base mb-6 max-w-lg">
                  Proudly serving the Okinawa community since 2020. We bring precision, reliability, and ninja-like efficiency to every lawn we care for.
                </p>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-4">
                  <div className="flex items-center gap-2 px-4 py-2 bg-[#39FF14]/10 rounded-full">
                    <Check className="w-4 h-4 text-[#39FF14]" />
                    <span className="text-white text-sm">On & Off Base</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-[#39FF14]/10 rounded-full">
                    <Check className="w-4 h-4 text-[#39FF14]" />
                    <span className="text-white text-sm">Licensed & Insured</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ backgroundColor: `${JD_YELLOW}15` }}>
                    <Star className="w-4 h-4" style={{ color: JD_YELLOW }} />
                    <span className="text-white text-sm">5-Star Service</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 px-6 md:px-12 py-16 md:py-24">
        {/* Tropical decorations */}
        <div className="absolute top-8 left-8 opacity-20 hidden lg:block">
          <Palmtree className="w-16 h-16 text-[#39FF14]" />
        </div>
        <div className="absolute bottom-8 right-8 opacity-20 hidden lg:block">
          <HibiscusIcon className="w-16 h-16" style={{ color: JD_YELLOW }} />
        </div>
        
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase mb-6">
            Ready to Get <span className="text-[#39FF14] neon-text">Started?</span>
          </h2>
          <p className="text-[#A1A1AA] text-base mb-8 max-w-2xl mx-auto">
            Contact us today to schedule your lawn care service. We serve both on-base and off-base locations throughout Okinawa.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              data-testid="cta-login-btn"
              onClick={() => openAuthModal("login")}
              className="bg-[#39FF14] text-black hover:bg-[#32D711] hover:shadow-[0_0_15px_rgba(57,255,20,0.6)] transition-all duration-300 font-bold uppercase tracking-wider rounded-full px-10 py-6 text-lg"
            >
              Customer Portal
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
            <a 
              href="https://www.facebook.com/zigzagokinawa" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button
                variant="outline"
                className="border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black rounded-full px-10 py-6 text-lg transition-all duration-300 w-full"
              >
                <Facebook className="mr-2 w-5 h-5" />
                Message Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Grass Ninja Certified Badge */}
      <div className="relative z-10 flex flex-col items-center py-6" data-testid="grass-ninja-badge">
        <p className="text-[#A1A1AA] text-xs uppercase tracking-widest mb-2">Our operators are</p>
        <img
          src="/images/grass-ninja-certified.png"
          alt="Grass Ninja Certified"
          className="h-36 w-auto object-contain"
          style={{ filter: "drop-shadow(0 0 8px rgba(57,255,20,0.3))" }}
        />
      </div>

      {/* Footer */}
      <footer className="relative z-10 px-6 md:px-12 py-8 border-t border-[#27272A] bg-[#0A0A0A]">
        {/* Decorative stripe */}
        <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: `linear-gradient(90deg, #39FF14, ${JD_YELLOW}, #39FF14)` }}></div>
        
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="Zig Zag Lawncare" 
              className="h-12 w-auto object-contain rounded-lg"
            />
            <div className="hidden md:flex items-center gap-2">
              <Sun className="w-5 h-5" style={{ color: JD_YELLOW }} />
              <Palmtree className="w-5 h-5 text-[#39FF14]" />
              <Waves className="w-5 h-5 text-blue-400" />
            </div>
          </div>
          <div className="flex items-center gap-6 text-[#A1A1AA] text-sm">
            <span className="hidden md:inline" style={{ color: JD_YELLOW }}>🌺 Serving Okinawa Since 2020</span>
            <a 
              href="https://www.facebook.com/zigzagokinawa" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-[#39FF14] transition-colors"
            >
              <Facebook className="w-4 h-4" />
              @zigzagokinawa
            </a>
          </div>
          <p className="text-[#A1A1AA] text-sm flex items-center gap-2">
            © 2026 Zig Zag Lawncare Okinawa
            <HibiscusIcon className="w-4 h-4" style={{ color: JD_YELLOW }} />
          </p>
        </div>
      </footer>

      {/* Booking Wizard Modal */}
      <BookingWizard 
        isOpen={showBookingWizard} 
        onClose={() => setShowBookingWizard(false)} 
      />

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode={authMode}
      />

      {/* Floating Lawnmower Widget - Bottom Left */}
      <button
        data-testid="floating-mower-widget"
        onClick={() => setShowBookingWizard(true)}
        className="fixed bottom-6 left-6 z-40 group cursor-pointer"
        aria-label="Get a Quote"
      >
        {/* Glow effect */}
        <div className="absolute inset-0 bg-[#39FF14] rounded-full blur-xl opacity-40 group-hover:opacity-70 transition-opacity duration-300 scale-75"></div>
        
        {/* Main widget container */}
        <div className="relative bg-[#121212] border-2 border-[#39FF14] rounded-full p-3 shadow-lg group-hover:shadow-[0_0_25px_rgba(57,255,20,0.6)] transition-all duration-300 group-hover:scale-110">
          {/* Lawnmower image */}
          <img 
            src={MOWER_URL}
            alt="Get a Quote"
            className="w-14 h-14 md:w-16 md:h-16 object-contain"
          />
          
          {/* Tooltip */}
          <div className="absolute bottom-full left-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
            <div className="bg-[#39FF14] text-black text-xs font-bold px-3 py-2 rounded-lg whitespace-nowrap shadow-lg">
              GET A QUOTE!
              <div className="absolute top-full left-6 w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-[#39FF14]"></div>
            </div>
          </div>
        </div>
        
        {/* Animated pulse ring */}
        <div className="absolute inset-0 border-2 border-[#39FF14] rounded-full animate-ping opacity-30"></div>
      </button>

      {/* Facebook Messenger Widget - Bottom Right */}
      <a
        href="https://m.me/zigzagokinawa"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="fb-messenger-widget"
        className="fixed bottom-6 right-6 z-50 group cursor-pointer"
        aria-label="Message us on Facebook"
      >
        {/* Glow effect */}
        <div className="absolute inset-0 bg-[#1877F2] rounded-full blur-xl opacity-40 group-hover:opacity-70 transition-opacity duration-300 scale-75"></div>
        
        {/* Main widget container */}
        <div className="relative bg-[#1877F2] border-2 border-white/30 rounded-full p-4 shadow-lg group-hover:shadow-[0_0_25px_rgba(24,119,242,0.6)] transition-all duration-300 group-hover:scale-110">
          {/* Messenger icon */}
          <MessageCircle className="w-8 h-8 md:w-10 md:h-10 text-white" />
        </div>
        
        {/* Tooltip - appears on hover */}
        <div className="absolute bottom-full right-0 mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          <div className="bg-[#1877F2] text-white text-xs font-bold px-4 py-3 rounded-lg whitespace-nowrap shadow-lg max-w-[200px]">
            <span className="block text-sm">Don't know where to Start?</span>
            <span className="block mt-1 text-white/90">Message us now!</span>
            <div className="absolute top-full right-6 w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-[#1877F2]"></div>
          </div>
        </div>
        
        {/* Animated pulse ring */}
        <div className="absolute inset-0 border-2 border-[#1877F2] rounded-full animate-ping opacity-30"></div>
      </a>
      {/* Neighborhood Discount Coupon Widget */}
      {neighborhoodDiscountEnabled && (
        <NeighborhoodCoupon
          cms={cms}
          onClaim={() => openAuthModal("signup")}
        />
      )}
    </div>
  );
};

export default LandingPage;
